import { Component, Inject, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { FieldDefinition } from '../../../models/field-definition';
import { UpdateCaseRequest } from '../../../models/StreamingModels';
import { BackendService } from '../../../services/backend.service';
import { buildFormDataSnapshot } from '../../../shared/dynamic-form/form-data-snapshot';
import { SelectCaseDialogComponent } from '../../case-management/select-case-dialog/select-case-dialog.component';

export interface NewCaseDialogData {
  caseDetailsId: number | null;
  recordingSessionId: number;
  roomName: string;
  caseId: number | null;
  caseNumber: string;
  judgeName: string;
  formFields: FieldDefinition[];
  formValues: Record<string, unknown>;
  buildCaseFormValues: (caseData: any) => Record<string, unknown>;
  selectedFormName: string;
  isCourt: boolean;
  orgExtId: string;
  courtRoom: string;
  courtLocation: string;
}

export interface NewCaseDialogResult {
  caseId: number | null;
  caseNumber: string | null;
  caseDetailsId: number | null;
  /** JSON snapshot of the dynamic form captured in this popup, or null when nothing was entered. */
  formData: string | null;
}

@Component({
  selector: 'app-new-case-dialog',
  templateUrl: './new-case-dialog.component.html',
  styleUrls: ['./new-case-dialog.component.css']
})
export class NewCaseDialogComponent implements OnDestroy {
  readonly formFields = (this.data.formFields || []).map(field => ({ ...field, required: false }));
  selectedCaseId: number | null = this.data.isCourt ? this.data.caseId : null;
  dynamicForm: FormGroup | null = null;
  secondsRemaining = 180;
  isSubmitting = false;
  isCreatingCase = true;
  isLoadingCase = false;
  private closeTimer = 0;
  private autoCloseCancelled = false;
  private caseSelectorRef: MatDialogRef<SelectCaseDialogComponent> | null = null;
  private readonly formSubscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NewCaseDialogData,
    private readonly dialogRef: MatDialogRef<NewCaseDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly backendService: BackendService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnDestroy(): void {
    window.clearInterval(this.closeTimer);
    this.formSubscription.unsubscribe();
    this.caseSelectorRef?.close();
  }

  // Once the operator starts filling the form the popup must not auto-close and throw the entry away.
  private cancelAutoClose(): void {
    this.autoCloseCancelled = true;
    if (this.closeTimer) {
      window.clearInterval(this.closeTimer);
      this.closeTimer = 0;
    }
  }

  get isAutoCloseActive(): boolean {
    return this.closeTimer > 0;
  }

  onDynamicFormReady(form: FormGroup): void {
    this.dynamicForm = form;
    this.formSubscription.add(form.valueChanges.subscribe(() => this.cancelAutoClose()));
  }

  setCreatedCaseDetailsId(id: number): void {
    this.data.caseDetailsId = id;
    this.isCreatingCase = false;
    if (!this.autoCloseCancelled) {
      this.closeTimer = window.setInterval(() => {
        this.secondsRemaining--;
        if (this.secondsRemaining <= 0) {
          this.ignore();
        }
      }, 1000);
    }
  }

  selectCase(): void {
    this.cancelAutoClose();
    this.caseSelectorRef = this.dialog.open(SelectCaseDialogComponent, {
      width: '1200px',
      maxWidth: '95vw',
      data: {
        courtRoom: this.data.courtRoom,
        courtLocation: this.data.courtLocation,
        orgExtId: this.data.orgExtId
      }
    });

    this.caseSelectorRef.afterClosed().subscribe((caseId: number | null) => {
      this.caseSelectorRef = null;
      if (caseId !== null) {
        this.loadSelectedCase(caseId);
      }
    });
  }

  private loadSelectedCase(caseId: number): void {
    this.isLoadingCase = true;
    this.backendService.getCaseById(caseId).subscribe((response: any) => {
      const selectedCase = response?.data ?? response;
      this.isLoadingCase = false;
      if (!selectedCase) {
        this.snackBar.open('Unable to load the selected case.', 'Close', { duration: 3000 });
        return;
      }

      this.selectedCaseId = selectedCase.id ?? caseId;
      this.data.caseNumber = selectedCase.caseNo || this.data.caseNumber;
      this.data.judgeName = this.resolveJudgeName(selectedCase.casePeople) || this.data.judgeName;
      this.data.formValues = this.data.buildCaseFormValues(selectedCase);
    }, () => {
      this.isLoadingCase = false;
      this.snackBar.open('Unable to load the selected case.', 'Close', { duration: 3000 });
    });
  }

  private resolveJudgeName(casePeople: any[]): string {
    const judge = (casePeople || []).find(person =>
      String(person?.role || '').toLowerCase().indexOf('judge') > -1);
    return judge?.people?.[0]?.name ? String(judge.people[0].name).trim() : '';
  }

  clearCase(): void {
    this.selectedCaseId = null;
  }

  clearForm(): void {
    this.clearCase();
    this.dynamicForm?.reset();
  }

  get hasFormValues(): boolean {
    return this.hasEnteredFormData(this.dynamicForm?.getRawValue() ?? {});
  }

  submit(): void {
    if (this.isSubmitting || this.isCreatingCase || !this.data.caseDetailsId || !this.hasFormValues) {
      return;
    }
    this.cancelAutoClose();

    let payload: UpdateCaseRequest;
    try {
      const formValues = this.dynamicForm ? this.dynamicForm.getRawValue() : {};
      const uniqueField = this.formFields.find(field => field.unique);
      const uniqueFieldValue = uniqueField ? formValues[uniqueField.key] : null;
      const caseNumber = this.nullIfBlank(uniqueFieldValue) || this.nullIfBlank(this.data.caseNumber);
      const rawUserId = localStorage.getItem('id');
      payload = {
        caseDetailsId: this.data.caseDetailsId,
        recordingSessionId: this.data.recordingSessionId,
        roomName: this.data.roomName,
        caseId: this.data.isCourt ? this.selectedCaseId : null,
        caseNumber,
        judgeName: this.nullIfBlank(this.data.judgeName),
        formData: this.hasEnteredFormData(formValues)
          ? JSON.stringify(buildFormDataSnapshot(this.formFields, formValues))
          : null,
        userName: localStorage.getItem('userName') || '',
        userId: rawUserId == null || rawUserId === '' ? null : Number(rawUserId)
      };
    } catch (error) {
      console.error('Unable to build the case update payload:', error);
      this.snackBar.open('Unable to read the case details. Please try again.', 'Close', { duration: 4000 });
      return;
    }

    this.isSubmitting = true;
    this.backendService.updateCase(payload).subscribe(() => {
      this.isSubmitting = false;
      this.snackBar.open('Case updated successfully.', 'Close', { duration: 3000 });
      const result: NewCaseDialogResult = {
        caseId: payload.caseId ?? null,
        caseNumber: payload.caseNumber,
        caseDetailsId: payload.caseDetailsId,
        formData: payload.formData ?? null
      };
      this.dialogRef.close(result);
    }, (error) => {
      console.error('Unable to update the case:', error);
      this.isSubmitting = false;
      this.snackBar.open('Unable to update the case. Please try again.', 'Close', { duration: 4000 });
    });
  }

  ignore(): void {
    if (this.isCreatingCase) {
      return;
    }
    this.dialogRef.close();
  }

  private nullIfBlank(value: unknown): string | null {
    const text = value == null ? '' : String(value).trim();
    return text || null;
  }

  private hasEnteredFormData(values: Record<string, unknown>): boolean {
    return Object.keys(values).some(key => {
      const value = values[key];
      return value === true || (value !== false && value != null && String(value).trim() !== '');
    });
  }
}